import React from "react";
import { alpha, GridLegacy as Grid, Table, TableBody, TableCell, TableContainer, TableRow, TablePagination, CircularProgress, Typography, InputAdornment, Checkbox, TextField, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { isValid } from "date-fns";
import { useAppSelector } from "@/redux/hooks";
import clsx from "clsx";
import { blue, green, orange, purple } from "@mui/material/colors";
import ActionsHeadlines from "./compliance/ActionsHeadlines";
import ActionRatingChip from "./ActionRatingChip";
import { moduleAreaDropdownValues } from "../constants/moduleAreaConstants";
import ResponsiveSelect from "../../components/ui/ResponsiveSelect";
import { actionStatusNames } from "../constants/actionStatusConstants";
import { ratingList } from "../constants/actionRatingConstants";
import {
    WifiTethering as OnTheSpotIcon,
    CheckBoxOutlineBlank,
    CheckBox,
    Search as SearchIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import DateFnsUtils from "@date-io/date-fns";
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider,
} from "@/compat/pickers";
import { useAuth } from "../../contexts/authContext";
import useContactsForAppAndSite from "../../hooks/useContactsForAppAndSite";
import { sortOrderName } from "../constants/sortOrder";
import EnhancedTableHead from "../../components/table/EnhancedTableHead";
import { Autocomplete } from "@/compat/Autocomplete";
import moduleAreas from "../constants/moduleAreaConstants";
import { getAuditTypeDescription } from "../../constants/auditConstants";

const uncheckedIcon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const useStyles = makeStyles((theme) => ({
    moduleChip: {
        color: "white",
        borderRadius: "4px",
        "&.module-0": {
            backgroundColor: green[400],
        },
        "&.module-1": {
            backgroundColor: purple[400],
        },
        "&.module-2": {
            backgroundColor: orange[400],
        },
        "&.module-4": {
            backgroundColor: blue[400],
        },
    },
    tableRow: {
        cursor: "pointer",
        "&:hover": {
            backgroundColor: alpha(theme.palette.secondary.main, 0.08),
        },
    },
    completeTableRow: {
        background: alpha(theme.palette.success.main, 0.08),
    },

    checkboxContainer: {
        display: "flex",
        alignItems: "center",
    },
    overdueCell: {
        color: theme.palette.error.dark,
    },
    overdueCellText: {
        color: theme.palette.error.dark,
        marginLeft: theme.spacing(1),
    },
    checkbox: {
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    dueCellContainer: {
        display: "flex",
        alignItems: "start",
    },
    dueCellCompleteIcon: {
        color: theme.palette.success.main,
        marginRight: theme.spacing(1),
    },
    toolbar: {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    notSetText: {
        color: theme.palette.error.dark,
    },
    iconBtn: {
        padding: theme.spacing(1),
    },
    assignedString: {
        maxWidth: "86%",
    },
    searchContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(2),
    },
    searchTextField: {
        width: '300px',
        '& .clearIcon': {
            visibility: 'hidden',
        },
        '&:hover .clearIcon': {
            visibility: 'visible',
        },
    },
    clearIcon: {
        color: theme.palette.grey[700],
    },
    endAdornment: {
        paddingRight: theme.spacing(1),
    },
    tooltip: {
        margin: theme.spacing(1),
        backgroundColor: 'white',
        border: '1px solid #000',
        color: theme.palette.common.black,
        fontSize: '0.9em',
        borderRadius: 0,
        padding: theme.spacing(1),
    },
    descriptionCell: {
        maxWidth: '300px',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflow: 'hidden',
    },
    moduleCellContainer: {
        display: "flex",
        alignItems: "center",
    },
    moduleCellText: {
        marginLeft: theme.spacing(1),
    },
}));

const defaultDropdownOption = { value: null, label: "All" };
const notSetDropdownOption = { value: "NotSet", label: "Not Set" };


function ActionsTable({
    isModule = false,
    actions = [],
    count = 0,
    onPageChange,
    onRowsChange,
    onModuleChange,
    onFromDateChange,
    onToDateChange,
    onOrderByChange,
    onSortByChange,
    page,
    rows,
    module = -1,
    fromDate,
    toDate,
    orderBy,
    sortBy,
    summaryData,
    rating,
    onRatingChange,
    assignees = [],
    onAssigneesChange,
    filteredAssignees = [],
    handleCurrentAssigneeChange,
    onFilteredTextChange,
    filteredText,
    onFilteredTextSearch,
    clearFilteredText,
    handleRowClicK,
    ModuleFormat,
    dueDateFormat,
    selectedAuditType,
    onAuditTypeChange,
    data
}) {
    const classes = useStyles();
    const { sites } = useAppSelector((state) => state.account);
    const { user } = useAuth();
    const contacts = useContactsForAppAndSite({ selectedSiteId: null });    

    const isFromDateValid = isValid(new Date(fromDate));
    const isToDateValid = isValid(new Date(toDate));

    const [searchRequested, setSeachRequested] = React.useState(false);

    const filteredModuleAreaDropdownValues = user?.details?.userAccessPermission
        ? moduleAreaDropdownValues.filter(
            (value) =>
                user?.details?.userAccessPermission[value.userAccessPermission] ===
                true
        )
        : defaultDropdownOption;

    const filteredAsigneeOptions = contacts?.data && assignees
        ? (assignees.includes(null) || assignees.includes('') ? [notSetDropdownOption] : [])
            .concat(Object.values(contacts.data)
                .filter(user => assignees.includes(user.externalId) && user.firstName)
                .sort((a, b) => a.firstName.localeCompare(b.firstName))
                .map(user => ({ value: user.externalId, label: `${user.firstName} ${user.lastName}` }))
        ) : [];

    const allHeadCells = [
        {
            id: "description",
            label: "Description",
            isSortable: true,
            display: true,
        },
        {
            id: "priority",
            label: "Priority",
            isSortable: true,
            display: true,
        },
        {
            id: "site",
            label: "Site",
            isSortable: true,
            display: true,
        },
        {
            id: "module",
            label: "Module",
            isSortable: true,
            active: false,
            display: !isModule,
        },
        {
            id: "auditType",
            label: "Audit Type",
            display: module === moduleAreas.AUDIT,
        },
        {
            id: "responsible",
            label: "Responsible",
            isSortable: true,
            display: true,
        },
        {
            id: "dueDate",
            label: "Due",
            isSortable: true,
            display: true,
        },
    ];

    const headCells = allHeadCells.filter(item => item.display);

    const UserName = ({ userId }) => {
        if (contacts.isLoading)
            return <CircularProgress size={12} />;

        if (!userId)
            return (
                <Typography variant="body2" className={classes.notSetText}>
                    Not Set
                </Typography>
            );

        if (contacts.isError || !contacts?.data[userId])
            return <Typography variant="body2">Unknown</Typography>;

        const fullname = `${contacts?.data[userId]?.firstName} ${contacts?.data[userId]?.lastName}`;
        return <Typography variant="body2">{fullname}</Typography>;
    };

    const onSortClick = (event, sortColumn) => {
        const isAsc = sortBy === sortColumn && orderBy === sortOrderName.ASC;
        onOrderByChange(isAsc ? sortOrderName.DESC : sortOrderName.ASC);
        onSortByChange(sortColumn);
    };

    function handleSearchChange(e) {
        setSeachRequested(false);
        onFilteredTextChange(e.target.value)
    }

    const handleSearchClick = () => {
        setSeachRequested(true);
        onFilteredTextSearch();
    };

    const showFilteredTextError = () => {
        return (filteredText && filteredText.length > 0 && filteredText.length < 4 && searchRequested)
    }

    const auditTypeOptions = React.useMemo(() => {
        // Filter audit types to only those present in the response
        const typeOptions = (data?.auditTypes || [])
            .map(type => ({
                identifier: type,
                display: getAuditTypeDescription(type)
            }))
            .filter(type => type.display)
            .sort((a, b) => a.display.localeCompare(b.display));

        // Add None and All options at the beginning
        const options = [
            { identifier: "", display: "All" },
            ...typeOptions
        ];

        return options;
    }, [data?.auditTypes]);    

    return (
        <>
            <Grid container spacing={1}>
                {!isModule && (
                    <Grid item xs={12} md={6} xl={2} className={classes.toolbar}>
                        <ResponsiveSelect
                            options={[defaultDropdownOption].concat(filteredModuleAreaDropdownValues)}
                            optionLabelFunction={(x) => x.label}
                            optionValueKey={"value"}
                            size="small"
                            disabled={!isFromDateValid || !isToDateValid}
                            fullWidth
                            label="Module"
                            placeholder="Module"
                            onChange={onModuleChange}
                            value={module || ""}
                        />
                    </Grid>
                )}
                {isModule && module === moduleAreas.AUDIT && (
                    <Grid item xs={12} md={isModule ? 3 : 3} className={classes.toolbar}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel shrink>Audit Type</InputLabel>
                            <Select
                                displayEmpty
                                value={selectedAuditType || ""}
                                onChange={onAuditTypeChange}
                                label="Audit Type"
                                placeholder="Audit Type"
                            >
                                {auditTypeOptions.map(option => (
                                    <MenuItem key={option.identifier} value={option.identifier}>
                                        {option.display}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={12} md={6} xl={!isModule ? 3 : 3} className={classes.toolbar}>
                    <Autocomplete
                        multiple
                        value={filteredAssignees || []}
                        onChange={handleCurrentAssigneeChange}
                        onBlur={onAssigneesChange}
                        options={filteredAsigneeOptions}
                        disableCloseOnSelect
                        clearOnBlur
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.label === value.label}
                        renderOption={(option, { selected }) => (
                            <React.Fragment>
                                <Checkbox
                                    className={classes.checkbox}
                                    icon={uncheckedIcon}
                                    checkedIcon={checkedIcon}
                                    checked={selected}
                                />
                                {option.label}
                            </React.Fragment>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder="Responsible Person(s)"
                                fullWidth
                            />
                        )}
                        renderTags={selected => {
                            let renderTagsValue = selected
                                .map(elem => elem.label)
                                .join(", ");
                            return (
                                <Typography className={classes.assignedString}
                                    noWrap={true}
                                    color="textPrimary"
                                >
                                    {renderTagsValue}
                                </Typography>
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={isModule ? 2 : 2} className={classes.toolbar}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            fullWidth
                            size="small"
                            inputVariant="outlined"
                            placeholder="From date"
                            disabled={!isToDateValid}
                            format="dd/MM/yyyy"
                            value={fromDate}
                            onChange={(date) => onFromDateChange(date)}
                            maxDate={toDate}
                            className={classes.dateInput}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} md={isModule ? 2 : 2} className={classes.toolbar}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            fullWidth
                            size="small"
                            inputVariant="outlined"
                            placeholder="To date"
                            format="dd/MM/yyyy"
                            disabled={!isFromDateValid}
                            value={toDate}
                            onChange={(date) => onToDateChange(date)}
                            minDate={fromDate}
                            className={classes.dateInput}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} md={isModule ? 2 : 2} className={classes.toolbar}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel shrink>Priority</InputLabel>
                        <Select
                            value={rating || ""}
                            onChange={onRatingChange}
                            displayEmpty
                            label="Priority"
                        >
                            <MenuItem value="">All</MenuItem>
                            {ratingList.map(rating => (
                                <MenuItem key={rating.value} value={rating.value}>
                                    {rating.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <div className={classes.searchContainer}>
                <TextField
                    className={classes.searchTextField}
                    label="Search Description"
                    variant="outlined"
                    size="small"
                    value={filteredText || ""}
                    onChange={handleSearchChange}
                    helperText='Please enter at least 4 character to search on'
                    error={showFilteredTextError()}
                    slotProps={{
                        formHelperText: { hidden: !showFilteredTextError() },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    {filteredText && (
                                        <Tooltip title="Clear" placement="bottom-end" classes={{ tooltip: classes.tooltip }}>
                                            <IconButton className={classes.iconBtn}>
                                                <ClearIcon className={`clearIcon ${classes.clearIcon}`}
                                                    onClick={clearFilteredText}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Search" placement="bottom-end" classes={{ tooltip: classes.tooltip }}>
                                        <IconButton className={classes.iconBtn} onClick={handleSearchClick}>
                                            <SearchIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                            className: classes.endAdornment
                        }
                    }}
                />
            </div>
            {summaryData && <ActionsHeadlines data={summaryData} />}
            <TableContainer>
                <Table size="small">
                    <EnhancedTableHead
                        headCells={headCells}
                        sortColumn={sortBy}
                        sortDirection={orderBy}
                        onRequestSort={onSortClick}
                    />
                    <TableBody>
                        {actions?.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>No actions.</TableCell>
                            </TableRow>
                        ) : (
                            actions
                                .map((row) => (
                                    <TableRow
                                        key={`${row.id}-${row.isDri}`}
                                        onClick={() => handleRowClicK(row)}
                                        className={
                                            row.statusName === actionStatusNames.COMPLETED
                                                ? clsx(classes.tableRow, classes.completeTableRow)
                                                : classes.tableRow
                                        }
                                    >
                                        <TableCell className={classes.descriptionCell}>
                                            {row.isAdHoc ?
                                                <div className={classes.moduleCellContainer}>
                                                    <OnTheSpotIcon />
                                                    <Typography variant="body2" className={classes.moduleCellText}>
                                                        {row.description}
                                                    </Typography>
                                                </div> : row.description}
                                        </TableCell>
                                        <TableCell>
                                            <ActionRatingChip rating={row?.rating} />
                                        </TableCell>
                                        <TableCell>
                                            {sites[row.siteExternalId]?.name ?? "Unknown"}
                                        </TableCell>
                                        {!isModule &&
                                            <TableCell>
                                                <ModuleFormat name={row.moduleName} type={row.auditTypeIdentifier ? row.auditType : "-"} />
                                            </TableCell>
                                        }
                                        {module === moduleAreas.AUDIT &&
                                            <TableCell>
                                                {row.auditTypeIdentifier ? row.auditType : "-"}
                                            </TableCell>
                                        }                                        
                                        <TableCell>
                                            <UserName userId={row.assignedEmployeeExternalId} />
                                        </TableCell>
                                        <TableCell>{dueDateFormat(row.statusName, row.dueDate)}</TableCell>
                                    </TableRow>
                                ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={count}
                    rowsPerPage={rows}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsChange}
                />
            </TableContainer>
        </>
    );
}

export default ActionsTable;
